import React, { useState, useCallback } from "react";
import { shrinkRectOverlap } from "./collision"; // Import collision helper

const NPCLayer = ({ npcs, camX, camY, playerX, playerY, onNPCClick }) => {
  const [activeNPC, setActiveNPC] = useState(null);
  const [showDialogue, setShowDialogue] = useState(false);
  const [dialogueText, setDialogueText] = useState("");
  
  // Check if player is colliding with any NPC
  // This function can be called from GameMap to check collision
  const isCollidingWithNPC = useCallback((x, y) => {
    if (!npcs || npcs.length === 0) return false;
    
    return npcs.some(npc => {
      const npcWidth = Number(npc.width || 16);
      const npcHeight = Number(npc.height || 24);
      return shrinkRectOverlap(
        x, y, 32, 32, // Player hitbox (same as in TrainersLayer)
        npc.x, npc.y, npcWidth, npcHeight, // NPC hitbox
        0, 0, 0, 0 // No padding
      );
    });
  }, [npcs]);
  
  // Expose the collision check function through a ref that GameMap can access
  NPCLayer.isCollidingWithNPC = isCollidingWithNPC;

  const handleNPCClick = (npc) => {
    // If this NPC opens a modal, always let GameMap handle it.
    // (NPCs may still have dialogue text for the modal to display.)
    if (npc.modalType || npc.trainer_id) {
      onNPCClick(npc);
      return;
    }

    if (npc.dialogue) {
      // Show dialogue for this NPC
      setActiveNPC(npc);
      setDialogueText(npc.dialogue);
      setShowDialogue(true);
      return;
    }

    // Fallback: pass to parent
    onNPCClick(npc);
  };

  const closeDialogue = () => {
    setShowDialogue(false);
    setActiveNPC(null);
  };

  return (
    <>
      {/* Render NPCs */}
      {npcs && npcs.map(npc => (
        (() => {
          const npcWidth = Number(npc.width || 32);
          const npcHeight = Number(npc.height || 32);
          const playerFeetY = Number(playerY) + 32;
          const npcFeetY = Number(npc.y) + npcHeight;
          const baseZ = playerFeetY > npcFeetY ? 60 : 80;
          const npcZ = Number.isFinite(Number(npc.zIndex)) ? Number(npc.zIndex) : baseZ;

          return (
            <div
              key={npc.id}
              className="npc"
              onClick={() => handleNPCClick(npc)}
              style={{
                position: 'absolute',
                left: npc.x - camX,
                top: npc.y - camY,
                width: npcWidth,
                height: npcHeight,
                backgroundImage: `url(${npc.sprite})`,
                backgroundPosition: `-${(npc.step || 0) * 32}px -${(npc.direction || 0) * 32}px`,
                cursor: npc.disabled ? 'not-allowed' : 'pointer',
                zIndex: npcZ,
                imageRendering: 'pixelated',
                opacity: npc.disabled ? 0.55 : 1,
                filter: npc.disabled ? 'grayscale(1) brightness(0.75)' : 'none',
              }}
            />
          );
        })()
      ))}

      {/* Dialogue box */}
      {showDialogue && activeNPC && (
        <div 
          className="dialogue-box"
          style={{
            position: 'absolute',
            bottom: '10%',
            left: '10%',
            width: '80%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '10px',
            borderRadius: '5px',
            border: '2px solid #ffcc00',
            zIndex: 1000,
            fontFamily: 'Arial, sans-serif',
            fontSize: '14px'
          }}
        >
          <div className="dialogue-header" style={{ marginBottom: '5px', fontWeight: 'bold' }}>
            {activeNPC.name || 'NPC'}:
          </div>
          <div className="dialogue-content">
            {dialogueText}
          </div>
          <div 
            className="dialogue-close" 
            style={{ 
              marginTop: '10px', 
              textAlign: 'right', 
              color: '#ffcc00',
              cursor: 'pointer'
            }}
            onClick={closeDialogue}
          >
            [Click to close]
          </div>
        </div>
      )}
    </>
  );
};

export default NPCLayer;
